import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { reportTypeApi } from '../api';
import type { ReportTypeFormData, ReportTypeListFilters } from '../types';

const KEYS = {
  all: ['reportTypes'] as const,
  lists: () => [...KEYS.all, 'list'] as const,
  list: (filters: ReportTypeListFilters) => [...KEYS.lists(), filters] as const,
  detail: (id: string) => [...KEYS.all, 'detail', id] as const,
};

export function useReportTypes(filters: ReportTypeListFilters = {}) {
  return useQuery({
    queryKey: KEYS.list(filters),
    queryFn: async () => {
      const { data } = await reportTypeApi.list(filters);
      return data;
    },
  });
}

export function useReportTypeDetail(id: string | undefined) {
  return useQuery({
    queryKey: KEYS.detail(id!),
    queryFn: async () => {
      const { data } = await reportTypeApi.getById(id!);
      return data.reportType;
    },
    enabled: !!id,
  });
}

export function useCreateReportType() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (formData: ReportTypeFormData) => {
      const { data } = await reportTypeApi.create(formData);
      return data.reportType;
    },
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: KEYS.all });
    },
  });
}

export function useUpdateReportType() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<ReportTypeFormData> }) => {
      const { data: response } = await reportTypeApi.update(id, data);
      return response.reportType;
    },
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: KEYS.all });
    },
  });
}

export function useDeactivateReportType() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await reportTypeApi.deactivate(id);
    },
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: KEYS.all });
    },
  });
}
